import { whenMarketingConsent } from '@nulo/cookiehub';
import {
  buildStepOneProfile,
  buildStepTwoProperties,
  identifyProfile,
  subscribeToList,
  updateProfileProperties,
} from '@nulo/klaviyo';
import { createFocusTrap, safeFocus } from '@nulo/a11y';

const STORAGE_SUBMITTED = 'newsletterFormSubmitted';
const STORAGE_CLOSED = 'newsletterPopupClosedTimestamp';
const SESSION_EMAIL = 'klaviyoSubscriberEmail';

/**
 * @typedef {Object} NewsletterPopupI18n
 * @property {Record<string, string>} [errors]
 * @property {Record<string, string>} [stages]
 * @property {string} [pet_stage_placeholder]
 */

/**
 * @param {Record<string, unknown>} payload
 */
function pushDataLayer(payload) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
}

/**
 * @returns {boolean}
 */
function isMobileDevice() {
  if (navigator.userAgentData?.mobile) return true;

  const userAgent = navigator.userAgent || navigator.vendor || '';
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
}

/**
 * @extends {HTMLElement}
 */
class NuloNewsletterPopup extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {(() => void) | null} */
  #releaseTrap = null;

  /** @type {string | null} */
  #subscriberEmail = null;

  /** @type {string} */
  #step = '1';

  /** @type {NewsletterPopupI18n} */
  #i18n = {};

  connectedCallback() {
    if (this.dataset.bound === 'true') return;
    this.dataset.bound = 'true';

    this.#i18n = this.#parseI18n();

    if (this.dataset.enabled !== 'true') return;
    if (document.documentElement.classList.contains('shopify-design-mode')) return;

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const dialog = this.querySelector('dialog[data-popup-dialog]');
    if (!(dialog instanceof HTMLDialogElement)) return;

    this.#bindDialogControls(dialog, signal);
    this.#initSelectFilledState(signal);
    this.#initPetBlocks(signal);
    this.#bindPetTypeStageSync(signal);

    const petBlocks = this.querySelector('[data-pet-blocks]');
    if (petBlocks instanceof HTMLElement) {
      this.#renumberPetBlocks(petBlocks);
    }

    this.#syncSelectFilledState();

    if (this.#shouldSuppress()) return;

    whenMarketingConsent({ signal }).then((consented) => {
      if (!consented || signal.aborted) return;
      if (this.#isExcludedPath()) return;
      this.#bindScrollTrigger(dialog, signal);
    });
  }

  disconnectedCallback() {
    this.#closeDialog();
    this.#abortController?.abort();
    this.#abortController = null;
  }

  /**
   * @returns {boolean}
   */
  #shouldSuppress() {
    if (localStorage.getItem(STORAGE_SUBMITTED)) return true;

    const closedAt = localStorage.getItem(STORAGE_CLOSED);
    if (!closedAt) return false;

    const suppressionDays = parseInt(this.dataset.suppressionDays || '5', 10);
    const elapsed = Date.now() - parseInt(closedAt, 10);
    return elapsed <= suppressionDays * 24 * 60 * 60 * 1000;
  }

  /**
   * @returns {NewsletterPopupI18n}
   */
  #parseI18n() {
    const el = this.querySelector('[data-i18n]');
    if (!el?.textContent) return {};

    try {
      return JSON.parse(el.textContent);
    } catch {
      return {};
    }
  }

  /**
   * @param {string} key
   * @returns {string}
   */
  #errorMessage(key) {
    const errors = this.#i18n.errors || {};

    if (key === 'email') return errors.email_required || '';
    if (key === 'petType') return errors.pet_type_required || '';
    if (key === 'shopPlace') return errors.shop_place_required || '';
    if (key === 'zipCode') return errors.zip_code_required || '';

    if (/^pet\d+_Name$/.test(key)) return errors.pet_name_required || '';
    if (/^pet\d+_Type$/.test(key)) return errors.pet_type_field_required || '';
    if (/^pet\d+_Stage$/.test(key)) return errors.pet_stage_required || '';
    if (/^pet\d+_Birthday$/.test(key)) return errors.pet_birthday_required || '';

    return '';
  }

  /**
   * Path without locale subfolder prefix (e.g. /fr/pages/foo → /pages/foo).
   *
   * @returns {string}
   */
  #logicalPath() {
    const pathname = window.location.pathname;
    const root = (this.dataset.localeRoot || '').replace(/\/$/, '');

    if (root && pathname === root) return '/';
    if (root && pathname.startsWith(`${root}/`)) {
      const logical = pathname.slice(root.length);
      return logical.startsWith('/') ? logical : `/${logical}`;
    }

    return pathname;
  }

  /**
   * @returns {boolean}
   */
  #isExcludedPath() {
    const raw = this.dataset.excludedPaths || '';
    if (!raw.trim()) return false;

    const path = this.#logicalPath();
    return raw
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean)
      .some((excluded) => path === excluded || path.startsWith(`${excluded}/`));
  }

  /**
   * @param {HTMLDialogElement} dialog
   * @param {AbortSignal} signal
   */
  #bindScrollTrigger(dialog, signal) {
    const threshold = parseInt(this.dataset.scrollThreshold || '50', 10);

    const onScroll = () => {
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollHeight <= 0) return;

      const scrollPercentage = (window.scrollY / scrollHeight) * 100;
      if (scrollPercentage < threshold) return;

      window.removeEventListener('scroll', onScroll);
      this.#openDialog(dialog);
    };

    window.addEventListener('scroll', onScroll, { passive: true, signal });
    onScroll();
  }

  /**
   * @param {HTMLDialogElement} dialog
   * @param {AbortSignal} signal
   */
  #bindDialogControls(dialog, signal) {
    const closeButtons = this.querySelectorAll('[data-popup-close]');
    for (const button of closeButtons) {
      button.addEventListener('click', () => this.#handleClose(dialog), { signal });
    }

    dialog.addEventListener(
      'click',
      (event) => {
        if (event.target === dialog) this.#handleClose(dialog);
      },
      { signal }
    );

    dialog.addEventListener(
      'cancel',
      (event) => {
        event.preventDefault();
        this.#handleClose(dialog);
      },
      { signal }
    );

    const stepOneForm = this.querySelector('[data-step-one-form]');
    stepOneForm?.addEventListener('submit', (event) => {
      event.preventDefault();
      this.#handleStepOneSubmit(stepOneForm);
    }, { signal });

    const moreInfoBtn = this.querySelector('[data-popup-more-info]');
    moreInfoBtn?.addEventListener('click', () => this.#showStep('2'), { signal });

    const stepTwoForm = this.querySelector('[data-step-two-form]');
    stepTwoForm?.addEventListener('submit', (event) => {
      event.preventDefault();
      this.#handleStepTwoSubmit(stepTwoForm);
    }, { signal });
  }

  /**
   * @param {AbortSignal} signal
   */
  #initSelectFilledState(signal) {
    this.addEventListener(
      'change',
      (event) => {
        const target = event.target;
        if (!(target instanceof HTMLSelectElement)) return;
        target.classList.toggle(
          'nulo-newsletter-popup__select--filled',
          Boolean(target.value)
        );
      },
      { signal }
    );
  }

  #syncSelectFilledState() {
    for (const select of this.querySelectorAll('select')) {
      if (!(select instanceof HTMLSelectElement)) continue;
      select.classList.toggle(
        'nulo-newsletter-popup__select--filled',
        Boolean(select.value)
      );
    }
  }

  /**
   * @param {AbortSignal} signal
   */
  #initPetBlocks(signal) {
    const container = this.querySelector('[data-pet-blocks]');
    if (!container) return;

    container.addEventListener(
      'click',
      (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;

        const addBtn = target.closest('[data-add-pet]');
        if (addBtn instanceof HTMLElement && container.contains(addBtn)) {
          this.#addPetBlock();
          return;
        }

        const removeBtn = target.closest('[data-remove-pet]');
        if (!(removeBtn instanceof HTMLElement)) return;

        const block = removeBtn.closest('[data-pet-block]');
        if (!block || !container.contains(block)) return;

        const blocks = container.querySelectorAll('[data-pet-block]');
        if (blocks.length <= 1) return;

        block.remove();
        this.#renumberPetBlocks(container);
      },
      { signal }
    );
  }

  /**
   * @param {HTMLElement} container
   */
  #renumberPetBlocks(container) {
    const blocks = container.querySelectorAll('[data-pet-block]');

    blocks.forEach((block, index) => {
      const petIndex = index + 1;
      block.dataset.petIndex = String(petIndex);

      for (const field of block.querySelectorAll('[data-pet-field]')) {
        if (!(field instanceof HTMLElement)) continue;
        const fieldName = field.dataset.petField;
        if (!fieldName) continue;

        const name = `pet${petIndex}_${fieldName}`;
        field.setAttribute('name', name);
        field.id = `popup-pet${petIndex}-${fieldName}`;

        const errorEl = field
          .closest('.nulo-newsletter-popup__field')
          ?.querySelector('[data-field-error]');
        if (errorEl instanceof HTMLElement) {
          errorEl.setAttribute('data-field-error', name);
        }

        const label = field
          .closest('.nulo-newsletter-popup__field')
          ?.querySelector('label');
        if (label instanceof HTMLLabelElement && field.id) {
          label.htmlFor = field.id;
        }
      }

      const removeBtn = block.querySelector('[data-remove-pet]');
      const addBtn = block.querySelector('[data-add-pet]');
      if (removeBtn instanceof HTMLElement) {
        removeBtn.hidden = blocks.length <= 1;
        removeBtn.hidden = index === blocks.length - 1;
      }
      if (addBtn instanceof HTMLElement) {
        addBtn.hidden = index !== blocks.length - 1;
      }
    });
  }

  /**
   * @param {AbortSignal} signal
   */
  #bindPetTypeStageSync(signal) {
    this.addEventListener(
      'change',
      (event) => {
        const target = event.target;
        if (!(target instanceof HTMLSelectElement)) return;
        if (target.dataset.petField !== 'Type') return;

        const block = target.closest('[data-pet-block]');
        if (!block) return;

        const stageSelect = block.querySelector('[data-pet-field="Stage"]');
        if (!(stageSelect instanceof HTMLSelectElement)) return;

        this.#populateStageOptions(stageSelect, target.value);
      },
      { signal }
    );
  }

  /**
   * @param {HTMLSelectElement} stageSelect
   * @param {string} petType
   */
  #populateStageOptions(stageSelect, petType) {
    const previous = stageSelect.value;
    stageSelect.innerHTML = '';

    const stages = this.#i18n.stages || {};

    const placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.textContent =
      this.#i18n.pet_stage_placeholder || "What is your pet's life stage?";
    stageSelect.appendChild(placeholder);

    /** @type {{ value: string, label: string }[]} */
    let options = [];

    if (petType === 'Dog') {
      options = [
        { value: 'Puppy', label: stages.puppy || 'Puppy' },
        { value: 'Adult', label: stages.adult || 'Adult' },
        { value: 'Senior', label: stages.senior || 'Senior' },
      ];
    } else if (petType === 'Cat') {
      options = [
        { value: 'Kitten', label: stages.kitten || 'Kitten' },
        { value: 'Adult', label: stages.adult || 'Adult' },
        { value: 'Senior', label: stages.senior || 'Senior' },
      ];
    } else {
      options = [
        { value: 'Kitten', label: stages.kitten || 'Kitten' },
        { value: 'Puppy', label: stages.puppy || 'Puppy' },
        { value: 'Adult', label: stages.adult || 'Adult' },
        { value: 'Senior', label: stages.senior || 'Senior' },
      ];
    }

    for (const option of options) {
      const el = document.createElement('option');
      el.value = option.value;
      el.textContent = option.label;
      stageSelect.appendChild(el);
    }

    stageSelect.classList.toggle('nulo-newsletter-popup__select--filled', false);

    if (options.some((option) => option.value === previous)) {
      stageSelect.value = previous;
      stageSelect.classList.toggle(
        'nulo-newsletter-popup__select--filled',
        Boolean(previous)
      );
    }
  }

  /**
   * @param {HTMLElement} container
   */
  #addPetBlock() {
    const container = this.querySelector('[data-pet-blocks]');
    const template = this.querySelector('[data-pet-block-template]');
    if (!(container instanceof HTMLElement) || !(template instanceof HTMLTemplateElement)) return;

    const clone = template.content.firstElementChild?.cloneNode(true);
    if (!(clone instanceof HTMLElement)) return;

    container.appendChild(clone);
    this.#renumberPetBlocks(container);
    this.#syncSelectFilledState();
  }

  /**
   * @param {HTMLDialogElement} dialog
   */
  #openDialog(dialog) {
    if (dialog.open) return;

    dialog.showModal();
    document.documentElement.classList.add('nulo-newsletter-popup-open');

    this.#releaseTrap?.();
    this.#releaseTrap = createFocusTrap(dialog, () => this.#handleClose(dialog));

    const firstField = dialog.querySelector('input, select, textarea, button');
    if (firstField instanceof HTMLElement) safeFocus(firstField);

    pushDataLayer({
      event: 'popup_view',
      device: isMobileDevice() ? 'mobile' : 'desktop',
    });
  }

  /**
   * @param {HTMLDialogElement} dialog
   */
  #handleClose(dialog) {
    localStorage.setItem(STORAGE_CLOSED, String(Date.now()));
    this.#closeDialog(dialog);
  }

  /**
   * @param {HTMLDialogElement} [dialog]
   */
  #closeDialog(dialog) {
    const target =
      dialog instanceof HTMLDialogElement
        ? dialog
        : this.querySelector('dialog[data-popup-dialog]');

    if (!(target instanceof HTMLDialogElement) || !target.open) return;

    target.close();
    document.documentElement.classList.remove('nulo-newsletter-popup-open');

    this.#releaseTrap?.();
    this.#releaseTrap = null;
  }

  /**
   * @param {'1' | 'success' | '2' | 'complete'} step
   */
  #showStep(step) {
    this.#step = step;

    for (const panel of this.querySelectorAll('[data-popup-panel]')) {
      if (!(panel instanceof HTMLElement)) continue;
      panel.hidden = panel.dataset.popupPanel !== step;
    }

    this.dataset.currentStep = step;
  }

  /**
   * @param {HTMLFormElement} form
   */
  async #handleStepOneSubmit(form) {
    this.#clearErrors(form);

    const email = form.querySelector('[name="email"]')?.value?.toString().trim() || '';
    const petType = form.querySelector('[name="petType"]')?.value?.trim() || '';
    const shopPlaces = [...form.querySelectorAll('[name="shopPlace"]:checked')]
      .map((input) => input.value)
      .filter(Boolean);

    /** @type {Record<string, string>} */
    const errors = {};

    const err = this.#i18n.errors || {};

    if (!email) {
      errors.email = err.email_required || '';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      errors.email = err.email_invalid || '';
    }

    if (!petType) {
      errors.petType = err.pet_type_required || '';
    }

    if (!shopPlaces.length) {
      errors.shopPlace = err.shop_place_required || '';
    }

    if (Object.keys(errors).length > 0) {
      this.#renderErrors(form, errors);
      return;
    }

    const submitButton = form.querySelector('[type="submit"]');
    if (submitButton instanceof HTMLButtonElement) submitButton.disabled = true;

    const deviceType = isMobileDevice() ? 'mobile' : 'desktop';
    const sourceType = this.dataset.sourceType || 'Custom Popup';
    const sourceFormLocale = this.dataset.sourceFormLocale || 'EU/EN';
    const listId = this.dataset.listId || '';

    const profile = buildStepOneProfile({
      email,
      petType,
      shopPlace: shopPlaces.join(', '),
      sourceType,
      deviceType,
      sourceFormLocale,
    });

    try {
      await identifyProfile(profile);
      // Profile fields are set via identify; subscribe only opts in to the list
      // (matches klaviyo-test — extra properties on subscribe can cause API errors).
      await subscribeToList({
        listId,
        email,
        customSource: sourceType,
      });

      this.#subscriberEmail = email;
      sessionStorage.setItem(SESSION_EMAIL, email);
      localStorage.setItem(STORAGE_SUBMITTED, 'true');

      pushDataLayer({
        event: 'popup_submit',
        device: deviceType,
      });

      this.#showStep('success');
    } catch (error) {
      this.#renderApiError(
        form,
        error instanceof Error ? error.message : this.#i18n.errors?.generic || ''
      );
    } finally {
      if (submitButton instanceof HTMLButtonElement) submitButton.disabled = false;
    }
  }

  /**
   * @param {HTMLFormElement} form
   */
  async #handleStepTwoSubmit(form) {
    this.#clearErrors(form);

    const email = this.#subscriberEmail || sessionStorage.getItem(SESSION_EMAIL);
    if (!email) {
      this.#renderApiError(form, this.#i18n.errors?.session_expired || '');
      return;
    }

    const blocks = form.querySelectorAll('[data-pet-block]');
    /** @type {Record<string, string>} */
    const errors = {};

    blocks.forEach((block, index) => {
      const petIndex = index + 1;
      const zipCode = index === 0 ? form.querySelector('[name="zipCode"]')?.value?.toString().trim() : '';
      const petName = block.querySelector(`[name="pet${petIndex}_Name"]`)?.value?.toString().trim();
      const petType = block.querySelector(`[name="pet${petIndex}_Type"]`)?.value?.trim();
      const petStage = block.querySelector(`[name="pet${petIndex}_Stage"]`)?.value?.trim();
      const petBirthday = block.querySelector(`[name="pet${petIndex}_Birthday"]`)?.value?.toString().trim();

      if (index === 0 && !zipCode) {
        errors.zipCode = this.#errorMessage('zipCode');
      }
      if (!petName) {
        errors[`pet${petIndex}_Name`] = this.#errorMessage(`pet${petIndex}_Name`);
      }
      if (!petType) {
        errors[`pet${petIndex}_Type`] = this.#errorMessage(`pet${petIndex}_Type`);
      }
      if (!petStage) {
        errors[`pet${petIndex}_Stage`] = this.#errorMessage(`pet${petIndex}_Stage`);
      }
      if (!petBirthday) {
        errors[`pet${petIndex}_Birthday`] = this.#errorMessage(`pet${petIndex}_Birthday`);
      }
    });

    if (Object.keys(errors).length > 0) {
      this.#renderErrors(form, errors);
      return;
    }

    const submitButton = form.querySelector('[type="submit"]');
    if (submitButton instanceof HTMLButtonElement) submitButton.disabled = true;

    const formData = new FormData(form);
    const properties = buildStepTwoProperties(formData);

    try {
      await updateProfileProperties(email, properties);

      localStorage.setItem(STORAGE_SUBMITTED, 'true');

      pushDataLayer({
        event: 'pop_submit_additonal_info',
        device: isMobileDevice() ? 'mobile' : 'desktop',
      });

      this.#showStep('complete');
    } catch (error) {
      this.#renderApiError(
        form,
        error instanceof Error ? error.message : this.#i18n.errors?.generic || ''
      );
    } finally {
      if (submitButton instanceof HTMLButtonElement) submitButton.disabled = false;
    }
  }

  /**
   * @param {HTMLFormElement} form
   */
  #clearErrors(form) {
    form.querySelectorAll('[data-field-error]').forEach((node) => {
      node.textContent = '';
      node.hidden = true;
    });

    const apiError = form.querySelector('[data-api-error]');
    if (apiError instanceof HTMLElement) {
      apiError.hidden = true;
      apiError.textContent = '';
    }
  }

  /**
   * @param {HTMLFormElement} form
   * @param {Record<string, string>} errors
   */
  #renderErrors(form, errors) {
    for (const [key, message] of Object.entries(errors)) {
      const errorEl = form.querySelector(`[data-field-error="${key}"]`);
      if (errorEl instanceof HTMLElement) {
        errorEl.textContent = message;
        errorEl.hidden = false;
      }
    }
  }

  /**
   * @param {HTMLFormElement} form
   * @param {string} message
   */
  #renderApiError(form, message) {
    const apiError = form.querySelector('[data-api-error]');
    if (!(apiError instanceof HTMLElement)) return;
    apiError.textContent = message;
    apiError.hidden = false;
  }
}

if (!customElements.get('nulo-newsletter-popup')) {
  customElements.define('nulo-newsletter-popup', NuloNewsletterPopup);
}
